/**
 * Chat Session Model
 * Represents a chat session with metadata
 */
export class ChatSession {
    constructor({
        id,
        sessionId,
        startedAt,
        lastActivityAt,
        messageCount = 0,
        isActive = true,
        userId
    }) {
        this.id = id || crypto.randomUUID();
        this.sessionId = sessionId || crypto.randomUUID();
        this.startedAt = startedAt || new Date();
        this.lastActivityAt = lastActivityAt || new Date();
        this.messageCount = messageCount;
        this.isActive = isActive;
        this.userId = userId;
    }

    /**
     * Update last activity timestamp
     */
    updateActivity() {
        this.lastActivityAt = new Date();
    }

    /**
     * Increment message count
     */
    incrementMessageCount(count = 1) {
        this.messageCount += count;
        this.updateActivity();
    }

    /**
     * Mark session as inactive
     */
    deactivate() {
        this.isActive = false;
        this.updateActivity();
    }

    /**
     * Get session duration in minutes
     */
    getDurationMinutes() {
        const duration = (this.lastActivityAt - this.startedAt) / (1000 * 60);
        return Math.round(duration);
    }

    /**
     * Get formatted start time
     */
    getFormattedStartTime() {
        return this.startedAt.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    }

    /**
     * Get formatted start date
     */
    getFormattedStartDate() {
        const today = new Date();
        const sessionDate = new Date(this.startedAt);
        
        if (sessionDate.toDateString() === today.toDateString()) {
            return 'Today';
        }
        
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        if (sessionDate.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        }
        
        return sessionDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    }

    /**
     * Create session from API response
     */
    static fromApiResponse(apiData) {
        return new ChatSession({
            id: apiData.id,
            sessionId: apiData.session_id,
            startedAt: new Date(apiData.started_at),
            lastActivityAt: new Date(apiData.last_activity_at),
            messageCount: apiData.message_count || 0,
            isActive: apiData.is_active,
            userId: apiData.user_id
        });
    }

    /**
     * Convert to API format
     */
    toApiFormat() {
        return {
            session_id: this.sessionId,
            started_at: this.startedAt.toISOString(),
            last_activity_at: this.lastActivityAt.toISOString(),
            message_count: this.messageCount,
            is_active: this.isActive
        };
    }

    /**
     * Create a new session
     */
    static createNew(userId) {
        return new ChatSession({
            userId: userId,
            isActive: true
        });
    }

    /**
     * Create session from existing session ID
     */
    static fromSessionId(sessionId, userId = null) {
        return new ChatSession({
            sessionId: sessionId,
            userId: userId,
            isActive: true,
            startedAt: new Date(), // We don't have the real start time, so use current
            lastActivityAt: new Date()
        });
    }

    /**
     * Update session ID (when server creates new session)
     */
    updateSessionId(newSessionId) {
        this.sessionId = newSessionId;
        this.updateActivity();
    }
} 